import { api } from '@/lib/api-client'
import type { IssueSeverity } from '@/shared/constants/issue-status'

// ── Types ──────────────────────────────────────────────────────────────────

export interface ResponseQuestion {
  question_id: string
  question_text: string
  answer: string | number | boolean | null
  answer_type: 'text' | 'number' | 'boolean' | 'date' | 'time' | 'image' | 'single_choice' | 'multi_choice'
  photo_url: string | null
}

export interface ResponseSection {
  id: string
  title: string
  questions: ResponseQuestion[]
}

export interface SurveyResponseIssue {
  id: string
  issue_ref: string
  title: string
  description?: string
  severity: IssueSeverity
  status: 'open' | 'pending' | 'completed'
  photo_url?: string | null
  section_title?: string
  question_id?: string | null
  section_id?: string | null
}

export interface TaskDetail {
  id: string
  survey_ref: string
  status: 'pending' | 'in_progress' | 'completed' | 'missed'
  started_at: string | null
  completed_at: string | null
  submitted_at: string | null
  // Present on the /surveys/{id} response; absent (or null) on older cached data.
  survey_duration?: number | null
  questionnaire_version_id: string
  entity_id: string
  // Present on the /surveys/{id} response.
  scheduled_date?: string | null
  series_label?: string | null
  site: {
    id: string
    name: string
    address: string
    lat: number
    long: number
    geofence_radius_meters?: number
  }
  officer: {
    id: string
    name: string
  }
  responses: {
    sections: ResponseSection[]
    // Embedded issue list on the /surveys/{id} response. The UI currently
    // sources issues from the separate /tasks/{id}/issues call (getTaskIssues)
    // instead — this is kept optional for forward compatibility.
    issues?: SurveyResponseIssue[]
  }
  // NEW top-level blocks on /surveys/{id} — only populated while status ==
  // 'pending'. Unused by the UI today; kept optional/untyped-detail so
  // consumers don't need to change when the backend adds fields.
  carry_forward_summary?: {
    applicable?: boolean
    total?: number
    linked_to_question?: number
    unlinked?: number
    acknowledged?: number
    overdue?: number
  }
  carry_forward_issues?: unknown[]
  question_history?: unknown[]
}

export interface TaskIssue {
  id: string
  category?: string
  description?: string
  status?: string
  severity?: IssueSeverity
  // Questionnaire section the issue was raised against (e.g. "Kitchen &
  // Dining"). Optional — older issues raised before sections were captured
  // may not have it.
  section_title?: string
}

export interface TaskFilterParams {
  status?: string
  entity_id?: string
}

// ── API functions ──────────────────────────────────────────────────────────

export function getTasks(params?: TaskFilterParams) {
  return api.get<any>('/tasks', { params })
}

export function createTask(payload: { site_id: string; assigned_to: string; due_date: string }) {
  return api.post<any>('/tasks', payload)
}

function cleanUrl(url: string | null | undefined): string | null | undefined {
  if (!url) {return url}
  return url.replace(/%22/g, '').replace(/"/g, '').trim()
}

export function getTask(taskId: string): Promise<TaskDetail> {
  // /api/v1/tasks/{id} was removed from the backend in API v2 (2026-08-06).
  // /surveys/{id} is its superset replacement — same id (assignments.id).
  return api.get<TaskDetail>(`/surveys/${taskId}`).then((d) => {
    // Strip trailing %22 / " from all image URLs in responses
    if (d.responses?.sections) {
      for (const section of d.responses.sections) {
        for (const q of section.questions) {
          q.photo_url = cleanUrl(q.photo_url) ?? null
          if (q.answer_type === 'image' && typeof q.answer === 'string') {
            q.answer = cleanUrl(q.answer) ?? q.answer
          }
        }
      }
    }
    return d
  })
}

export function getTaskIssues(taskId: string): Promise<TaskIssue[]> {
  return api
    .get<TaskIssue[] | { issues: TaskIssue[] }>(`/tasks/${taskId}/issues`)
    .then((d) => (Array.isArray(d) ? d : d.issues ?? []))
}
